// locale-unextracted-ignore-file
import { Alert, Box, Typography } from '@mui/material';
import { HlsTimelineErrorEntry } from './useHlsTimelineErrors';

type HlsTimelineErrorDisplayProps = {
  errors: HlsTimelineErrorEntry[];
};

/**
 * Formats an error timestamp for the debug panel (local time, HH:mm:ss.SSS).
 */
function formatErrorTime(at: number): string {
  const date = new Date(at);
  const time = date.toLocaleTimeString(undefined, {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const ms = String(date.getMilliseconds()).padStart(3, '0');
  return `${time}.${ms}`;
}

/**
 * Debug-only list of HLS / media errors shown under the timeline chart.
 */
export function HlsTimelineErrorDisplay({ errors }: HlsTimelineErrorDisplayProps) {
  if (errors.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        HLS / playback errors ({errors.length})
      </Typography>
      {errors.map((entry) => (
        <Alert
          key={entry.id}
          severity={entry.fatal ? 'error' : 'warning'}
          variant="outlined"
          sx={{ typography: 'caption', alignItems: 'flex-start' }}
        >
          <Box component="span" sx={{ display: 'block' }}>
            <strong>{formatErrorTime(entry.at)}</strong>
            {entry.fatal ? ' [fatal]' : ' [non-fatal]'} — {entry.type} / {entry.details}
          </Box>
          <Box component="span" sx={{ display: 'block' }}>
            {entry.message}
          </Box>
          {entry.url ? (
            <Box
              component="span"
              sx={{ display: 'block', color: 'text.secondary', wordBreak: 'break-all' }}
            >
              {entry.url}
            </Box>
          ) : null}
        </Alert>
      ))}
    </Box>
  );
}
